import { FC, useCallback, useContext, useState } from "react";
import { useLocation, useNavigate } from "react-router";

import { Buttons, ButtonAppearance, Button, MenuButton, Color } from "capitalrx-components-2.0/lib/general";
import { AuthenticationContext, ThemeSelectorContext } from "@/context";
import { ANALYTIC_EVENTS, logEvent } from "@/service/analyticsService";
import { getThemeURL, hexToRgbColor } from "@/utils";
import styles from "./LoginButton.module.scss";


export const LoginButton: FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState<boolean>(false);
  const { isUserLoggedIn, logoutUser } = useContext(AuthenticationContext);
  const { header_text_color, secondary_hover_color } = useContext(ThemeSelectorContext);

  const handleLogin = useCallback(() => {
    logEvent(ANALYTIC_EVENTS.AUTH.loginButtonClick);
    const loginPath = getThemeURL("LOGIN");
    const redirect = encodeURIComponent(location.pathname + location.search);
    navigate(`${loginPath}?redirect=${redirect}`);

  }, [navigate, location]);

  const handleLogout = useCallback(async () => {
    setLoading(true);
    logEvent(ANALYTIC_EVENTS.AUTH.logoutButtonClick);

    try {
      await logoutUser();
    } catch (error) {
      console.error("Failed to log out");
      logEvent(ANALYTIC_EVENTS.ERRORS.logoutFailure);
    }
    setLoading(false);

  }, []);

  const handleAccountSettings = useCallback(async () => {
    logEvent(ANALYTIC_EVENTS.AUTH.accountSettingsButtonClick);
    navigate(getThemeURL("ACCOUNT"));
  }, [navigate]);

  const handleProviderDocuments = useCallback(async () => {
    logEvent(ANALYTIC_EVENTS.PROVIDER_MANAGEMENT.documentsButtonClicked);
    navigate(getThemeURL("PROVIDER_DOCUMENTS"));
  }, [navigate]);

  const handleNotificationSettings = useCallback(async () => {
    navigate(getThemeURL("NOTIFICATIONS"));
  }, [navigate]);

  const renderMenuButton = useCallback(() => {
    const commonItems = [
      { handler: handleAccountSettings, testId: "accountSettings", label: "Provider Profile" },
      { handler: handleProviderDocuments, testId: "documents", label: "Documents" },
      { handler: handleNotificationSettings, testId: "notificationSettings", label: "Notification Settings" },
      { handler: handleLogout, testId: "logout", label: "Log Out" },
    ];

    const CustomAppearance: ButtonAppearance = {
      ...Buttons.Functional.Tertiary.Large,
      padding: {
        horizontal: 12,
        vertical: 4,
      },
      label: {
        ...Buttons.Functional.Tertiary.Large.label,
        uppercase: false,
        weight: "regular",
        letterSpacing: 0,
        lineHeight: 24,
      },
      normal: {
        ...Buttons.Functional.Tertiary.Large.normal,
        foregroundColor: hexToRgbColor(header_text_color),
        borderColor: hexToRgbColor(header_text_color),
        textUnderline: false,
      },
      hover: {
        ...Buttons.Functional.Tertiary.Large.hover,
        foregroundColor: hexToRgbColor(header_text_color),
        backgroundColor: Color.withAlpha(hexToRgbColor(secondary_hover_color), 0.1),
        borderColor: hexToRgbColor(header_text_color),
        textUnderline: false,
      },
      pressed: {
        ...Buttons.Functional.Tertiary.Large.pressed,
        foregroundColor: hexToRgbColor(header_text_color),
        backgroundColor: Color.withAlpha(Color.White, 0.1),
        textUnderline: false,
      },
      disabled: {
        ...Buttons.Functional.Tertiary.Large.disabled,
        foregroundColor: Color.White,
        textUnderline: false,
      },
    };

    return (
      <MenuButton className={styles.Actions} label="Account" appearance={CustomAppearance}>
        <MenuButton.Group>
          {commonItems.map(({ handler, testId, label }) => (
            <MenuButton.Item
              className={styles.ActionMenuItem}
              key={testId}
              onClick={() => handler()}
              disabled={loading}
              testId={testId}
              type="normal"
            >
              {label}
            </MenuButton.Item>
          ))}
        </MenuButton.Group>
      </MenuButton>
    );
  }, [
    handleLogout,
    loading,
  ]);
  const LoginAppearance: ButtonAppearance = {
      ...Buttons.Functional.Secondary.Large,
      normal: {
        ...Buttons.Functional.Secondary.Large.normal,
        foregroundColor: hexToRgbColor(header_text_color),
        borderColor: hexToRgbColor(header_text_color),
        textUnderline: false,
      },
      hover: {
        ...Buttons.Functional.Secondary.Large.hover,
        foregroundColor: hexToRgbColor(header_text_color),
        backgroundColor: Color.withAlpha(hexToRgbColor(secondary_hover_color), 0.1),
        borderColor: hexToRgbColor(header_text_color),
        textUnderline: false,
      },
      pressed: {
        ...Buttons.Functional.Secondary.Large.pressed,
        foregroundColor: hexToRgbColor(header_text_color),
        backgroundColor: Color.withAlpha(Color.White, 0.1),
        borderColor: hexToRgbColor(header_text_color),
        textUnderline: false,
      },
    };

  return (
    <div>
      {!isUserLoggedIn() && location.pathname !== "/login"
        && <Button
          appearance={LoginAppearance}
          onClick={() => handleLogin()}
          disabled={loading}
          testId="test__login_button"
        >
          Log In
        </Button>
      }
      {isUserLoggedIn() && renderMenuButton()}
    </div>
  );
};
